import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { currencyFormatter } from "@common/helpers/currency";
import { ScheduleRecognitionPeriod } from "@common/schemas/domains/accounting/scheduleReview.schema";
import { ScheduleRecognitionReviewColumn } from "@common/types/views/scheduleRecognitionReviewView";
import { AMOUNT_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewConfig } from "@lib/views";
import styles from "./RecognitionReviewCells.module.scss";
import {
    formatEffectiveDate,
    formatPeriodKeyLabel,
} from "./recognitionReviewHelpers";

type RecognitionCell = CellContext<
    ViewTableFeatures,
    ScheduleRecognitionPeriod,
    unknown
>;

const DateCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>
        {formatEffectiveDate(row.original.effectiveDate)}
    </ViewTableCell>
);
const ScheduleNameCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>{row.original.scheduleName}</ViewTableCell>
);
const SourceCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>{row.original.sourceLabel}</ViewTableCell>
);
const CounterpartyCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>{row.original.counterpartyName ?? "-"}</ViewTableCell>
);
const HoldingGlCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>{row.original.holdingAccountName}</ViewTableCell>
);
const RecognitionGlCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>{row.original.recognitionAccountName}</ViewTableCell>
);
const PeriodCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>
        {formatPeriodKeyLabel(row.original.periodKey)}
    </ViewTableCell>
);
const AmountCell = ({ row }: RecognitionCell) => (
    <ViewTableCell>
        <span className={styles.amount}>
            {currencyFormatter.format(row.original.amount)}
        </span>
    </ViewTableCell>
);

export const recognitionReviewViewConfig: ViewConfig<
    ScheduleRecognitionPeriod,
    ScheduleRecognitionReviewColumn,
    never
> = {
    columns: {
        [ScheduleRecognitionReviewColumn.DATE]: {
            accessorKey: "effectiveDate",
            name: "Date",
            header: "Date",
            cell: DateCell,
            immutable: true,
            minSize: 130,
            maxSize: 160,
        },
        [ScheduleRecognitionReviewColumn.SCHEDULE]: {
            accessorKey: "scheduleName",
            name: "Schedule",
            header: "Schedule",
            cell: ScheduleNameCell,
            minSize: 180,
            maxSize: 320,
        },
        [ScheduleRecognitionReviewColumn.SOURCE]: {
            accessorKey: "sourceLabel",
            name: "Source",
            header: "Source",
            cell: SourceCell,
            minSize: 180,
            maxSize: 400,
        },
        [ScheduleRecognitionReviewColumn.COUNTERPARTY]: {
            accessorKey: "counterpartyName",
            name: "Counterparty",
            header: "Counterparty",
            cell: CounterpartyCell,
            minSize: 160,
            maxSize: 320,
        },
        [ScheduleRecognitionReviewColumn.HOLDING_GL]: {
            accessorKey: "holdingAccountName",
            name: "Holding GL",
            header: "Holding GL",
            cell: HoldingGlCell,
            minSize: 160,
            maxSize: 320,
        },
        [ScheduleRecognitionReviewColumn.RECOGNITION_GL]: {
            accessorKey: "recognitionAccountName",
            name: "Recognition GL",
            header: "Recognition GL",
            cell: RecognitionGlCell,
            minSize: 160,
            maxSize: 320,
        },
        [ScheduleRecognitionReviewColumn.PERIOD]: {
            accessorKey: "periodKey",
            name: "Period",
            header: "Period",
            cell: PeriodCell,
            minSize: 140,
            maxSize: 180,
        },
        [ScheduleRecognitionReviewColumn.AMOUNT]: {
            accessorKey: "amount",
            name: "Amount",
            header: "Amount",
            cell: AmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
    },
};
